import React, { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Droplets, Calculator } from "lucide-react";
import VolumeCalculatorDialog from "@/components/VolumeCalculatorDialog";
import { DatePicker } from "@/components/DatePicker";

export default function BookingForm({ onCreated, defaultDate }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [date, setDate] = useState(() => defaultDate || new Date().toISOString().slice(0, 10));
  const [area, setArea] = useState(user?.pool_area ? String(user.pool_area) : "");
  const [depth, setDepth] = useState(user?.pool_depth ? String(user.pool_depth) : "");
  const [note, setNote] = useState("");
  const [avail, setAvail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);

  useEffect(() => {
    if (defaultDate) setDate(defaultDate);
  }, [defaultDate]);

  const volume = useMemo(() => {
    const a = parseFloat(area);
    const d = parseFloat(depth);
    if (!isFinite(a) || !isFinite(d)) return 0;
    return Math.round(a * d * 100) / 100;
  }, [area, depth]);

  useEffect(() => {
    if (!date) return;
    api.get(`/quota/availability?day=${date}`).then((r) => setAvail(r.data)).catch(() => setAvail(null));
  }, [date]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/bookings", {
        date,
        pool_area: parseFloat(area),
        pool_depth: parseFloat(depth),
        note,
      });
      toast.success(t("booking_request_sent"));
      setNote("");
      onCreated && onCreated();
      // refresh availability
      const r = await api.get(`/quota/availability?day=${date}`);
      setAvail(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const exceeded = avail && volume > (avail.available_m3 ?? 0);

  return (
    <Card className="border-border">
      <CardContent className="p-6">
        <div className="mb-4 flex items-center gap-2">
          <Droplets className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">{t("book_filling")}</h3>
        </div>
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={submit}>
          <div>
            <Label>{t("date")}</Label>
            <DatePicker value={date} onChange={setDate} testId="booking-date-input" />
          </div>
          <div>
            <Label>{t("volume_m3")}</Label>
            <div className="flex h-10 items-center rounded-md border border-input bg-muted/40 px-3 font-mono-jb text-sm" data-testid="booking-volume-display">
              {volume} m³
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label>{t("pool_area")}</Label>
              <button
                type="button"
                onClick={() => setCalcOpen(true)}
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                data-testid="open-volume-calc-btn"
              >
                <Calculator className="h-3.5 w-3.5" />
                {t("calc_open")}
              </button>
            </div>
            <Input type="number" step="0.1" min="0" required value={area} onChange={(e) => setArea(e.target.value)} data-testid="booking-area-input" />
          </div>
          <div>
            <Label>{t("pool_depth")}</Label>
            <Input type="number" step="0.1" min="0" required value={depth} onChange={(e) => setDepth(e.target.value)} data-testid="booking-depth-input" />
          </div>
          <div className="sm:col-span-2">
            <Label>{t("note")}</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="booking-note-input" />
          </div>

          {avail && (
            <div className="sm:col-span-2 rounded-md border border-border p-3 text-sm" data-testid="availability-panel">
              <div className="label-caps mb-1">{t("availability")}</div>
              <div className="flex items-baseline justify-between font-mono-jb">
                <span>{avail.used_m3} / {avail.quota_m3} m³ {t("used_today")}</span>
                <span className={`font-semibold ${exceeded ? "text-destructive" : "text-primary"}`}>
                  {avail.available_m3} m³
                </span>
              </div>
            </div>
          )}

          <Button type="submit" className="sm:col-span-2" disabled={loading || exceeded} data-testid="booking-submit-btn">
            {exceeded ? t("quota_exceeded") : loading ? "…" : t("submit_booking")}
          </Button>
        </form>
        <div className="mt-3 text-xs text-muted-foreground">{t("calc_hint")}</div>
      </CardContent>
      <VolumeCalculatorDialog
        open={calcOpen}
        onOpenChange={setCalcOpen}
        depth={depth}
        onApply={(newArea) => setArea(String(newArea))}
      />
    </Card>
  );
}
